import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import TuneIcon from "@mui/icons-material/Tune";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
  PQRSD_TYPES,
  PQRSD_PRIORITY,
  PQRSD_PRIORITY_LABELS,
  PQRSD_PRIORITY_COLORS,
} from "../../constants";
import { listPqrsdGroups } from "../../services/backend/PqrsdConfigService";
import {
  overridePqrsdClassification,
  retryPqrsdClassification,
} from "../../services/backend/PqrsdService";

import { pqrsdText } from "./pqrsdLabel";
// Surfaces what the AI did with a PQRSD (interpretation, type, priority, routed
// dependency, confidence) and — for needs_review/failed — WHY it couldn't
// classify. Supervisors get an override control to reclassify / re-route.
// annulled: a void PQRSD has nothing to route, and handlePqrsd refuses to
// reclassify it, so neither AI action is offered.
export default function PqrsdAiPanel({ pqrsdDoc, canOverride, annulled = false, onChanged }) {
  const user = useSelector((state) => state.user.value);
  const pqrsd = pqrsdDoc?.pqrsd || {};
  const ai = pqrsd.aiClassification || null;
  const aiStatus = pqrsd.aiStatus || null;

  // Re-running the classifier is a full-admin-only action (also enforced
  // server-side by the ADMIN role on the caller's token).
  const isFullAdmin = !!(user?.roles || []).includes("ADMIN");

  const [open, setOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);

  const retry = async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      await retryPqrsdClassification(pqrsdDoc.filedId, user);
      onChanged && onChanged();
    } catch (e) {
      setRetryError(e?.message || "No fue posible reintentar la clasificación.");
    } finally {
      setRetrying(false);
    }
  };

  // Nothing to show when AI never ran on this PQRSD (e.g. AI was off at intake)
  // — except for full admins, who can still trigger a classification retry,
  // which an annulled PQRSD doesn't get.
  if (!aiStatus && !ai && (!isFullAdmin || annulled)) return null;

  const confidencePct =
    ai && typeof ai.confidence === "number" ? `${Math.round(ai.confidence * 100)}%` : null;
  const needsAttention = aiStatus === "needs_review" || aiStatus === "failed";

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderColor: needsAttention ? "warning.main" : "divider" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <SmartToyIcon color={needsAttention ? "warning" : "primary"} />
        <Typography variant="h6">Clasificación por IA</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isFullAdmin && !annulled && (
          <Button
            size="small"
            startIcon={<AutorenewIcon />}
            onClick={retry}
            disabled={retrying}
          >
            {retrying ? "Reintentando…" : "Reintentar IA"}
          </Button>
        )}
        {canOverride && (
          <Button size="small" startIcon={<TuneIcon />} onClick={() => setOpen(true)}>
            {needsAttention ? "Clasificar y enrutar" : "Reclasificar"}
          </Button>
        )}
      </Stack>

      {retryError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRetryError(null)}>
          {retryError}
        </Alert>
      )}

      {!aiStatus && !ai && !annulled && (
        <Typography variant="body2" color="text.secondary">
          {pqrsdText("La IA no ha clasificado esta PQRSD. Usa «Reintentar IA» para analizarla ahora (requiere que la IA esté activada en Config PQRSD).")}
        </Typography>
      )}

      {needsAttention && (
        <Alert severity={aiStatus === "failed" ? "error" : "warning"} sx={{ mb: 2 }}>
          <AlertTitle>
            {aiStatus === "failed" ? pqrsdText("La IA no pudo analizar esta PQRSD") : "Requiere revisión manual"}
          </AlertTitle>
          {ai?.reason || "La IA no pudo clasificarla con confianza. Asígnala manualmente."}
        </Alert>
      )}

      {pqrsd.aiSummary && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Resumen de la IA</Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{pqrsd.aiSummary}</Typography>
        </Box>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {pqrsd.groupName && <Chip size="small" color="primary" label={`Dependencia: ${pqrsd.groupName}`} />}
        {pqrsd.priority && (
          <Chip
            size="small"
            color={PQRSD_PRIORITY_COLORS[pqrsd.priority] || "default"}
            label={`Prioridad: ${PQRSD_PRIORITY_LABELS[pqrsd.priority] || pqrsd.priority}`}
          />
        )}
        {ai?.type && <Chip size="small" variant="outlined" label={`Tipo IA: ${PQRSD_TYPES[ai.type] || ai.type}`} />}
        {confidencePct && <Chip size="small" variant="outlined" label={`Confianza: ${confidencePct}`} />}
      </Stack>

      {Array.isArray(ai?.attachmentNotes) && ai.attachmentNotes.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>Adjuntos no analizados</AlertTitle>
          {ai.attachmentNotes.join("; ")}
        </Alert>
      )}

      {open && (
        <OverrideDialog
          pqrsdDoc={pqrsdDoc}
          user={user}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); onChanged && onChanged(); }}
        />
      )}
    </Paper>
  );
}

function OverrideDialog({ pqrsdDoc, user, onClose, onSaved }) {
  const pqrsd = pqrsdDoc?.pqrsd || {};
  const [groups, setGroups] = useState([]);
  const [type, setType] = useState(pqrsd.type || "");
  const [priority, setPriority] = useState(pqrsd.priority || "");
  const [groupId, setGroupId] = useState(pqrsd.groupId || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => setGroups((await listPqrsdGroups()).filter((g) => g.active !== false)))();
  }, []);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === groupId) || null,
    [groups, groupId]
  );

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await overridePqrsdClassification(
        pqrsdDoc.filedId,
        { type: type || null, priority: priority || null, group: selectedGroup },
        user
      );
      onSaved();
    } catch (e) {
      setError(e?.message || "No fue posible actualizar la clasificación.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{pqrsdText("Clasificar y enrutar PQRSD")}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo</InputLabel>
            <Select label="Tipo" value={type} onChange={(e) => setType(e.target.value)}>
              {Object.entries(PQRSD_TYPES).map(([k, label]) => (
                <MenuItem key={k} value={k}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Prioridad</InputLabel>
            <Select label="Prioridad" value={priority} onChange={(e) => setPriority(e.target.value)}>
              <MenuItem value="">Sin prioridad</MenuItem>
              {Object.values(PQRSD_PRIORITY).map((p) => (
                <MenuItem key={p} value={p}>{PQRSD_PRIORITY_LABELS[p]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Dependencia</InputLabel>
            <Select label="Dependencia" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <MenuItem value="">Sin dependencia (enviar a Triage)</MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedGroup && (
            <TextField
              label="Integrantes que serán notificados"
              value={(selectedGroup.members || []).map((m) => m.name || m.email || m.userId).join(", ") || "—"}
              InputProps={{ readOnly: true }}
              size="small"
              multiline
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button onClick={submit} variant="contained" disabled={busy}>
          {busy ? "Guardando…" : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
